import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { faCalendar, faSearch, faTimesCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, OperatorFunction, switchMap, tap } from 'rxjs';
import { Address } from 'src/app/core/models/Address';
import { CUITData, CuitFormInitialDraft } from 'src/app/core/models/CUIT';
import { DetalleForAdd } from 'src/app/core/models/DetalleForAdd';
import { DocumentTypeEnum } from 'src/app/core/models/DocumentTypeEnum';
import { DocumentoFacturaEnum } from 'src/app/core/models/DocumentoFacturaEnum';
import { Factura } from 'src/app/core/models/Factura';
import { FacturaForAdd } from 'src/app/core/models/FacturaForAdd';
import { FacturaTypeEnum, NotaTypeEnum, TaxRole } from 'src/app/core/models/FacturaTypeEnum';
import { Person } from 'src/app/core/models/Person';
import { CuitService } from 'src/app/services/cuit.service';
import { DateService } from 'src/app/services/date.service';
import { FacturasElectronicasService } from 'src/app/services/facturas-electronicas.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-factura-form',
  templateUrl: './factura-form.component.html',
  styleUrls: ['./factura-form.component.css']
})
export class FacturaFormComponent implements OnInit {
  @Output() closeAction = new EventEmitter();
  @Output() cancelAction = new EventEmitter();
  @Input() facturaAsociada: Factura = null;
  @Input() consumidores: Person[] = [];
  @Input() terceros: Person[] = null;
  @Input() amount: number;
  @Input() contractId: number = null;
  @Input() feeId: number = null;
  @Input() paymentId: number = null;
  /** Detalle(s) precargados; si la factura cubre varios meses, un string por mes. */
  @Input() honorarioDetalle: string | string[];
  /** CUIT emisor usado la última vez en este contrato; si coincide con un emisor delegado, se autoselecciona. */
  @Input() lastInvoiceBy: number | string | null = null;

  iconCalendar = faCalendar;
  iconSearch = faSearch;
  iconClear = faTimesCircle;
  iconPlus = faPlus;
  submitted: boolean = false;
  facturaToSubmit: FacturaForAdd;
  facturaType: any[] = [];
  docType: any[] = [];
  emisores: CUITData[] = [];
  /** Filtro de la lista de emisores (nombre o CUIT); la elección sigue siendo solo sobre CUIT delegados. */
  filtroListaEmisor: string = '';
  /** CUIT del emisor elegido en el selector (debe existir en `emisores`). */
  emisorCuitSeleccionado: string | null = null;
  /** Borrador para alta de CUIT desde AFIP (CUIT aún no delegado). */
  datosAfipEmisorPendiente: CuitFormInitialDraft | null = null;
  cuitFormInitialDraft: CuitFormInitialDraft | null = null;
  minDate: Date;
  maxDate: Date;
  searchString: string = '';
  searching: boolean;
  buscandoCuitEmisor: boolean = false;
  cuitBuscadorEmisor: string = '';
  buscandoCuitConsumidor: boolean = false;
  cuitBuscadorConsumidor: string = '';
  consumidorTipoDocumentoAfip: string = '';
  @ViewChild('cuitFormTemplate') cuitFormTemplate: TemplateRef<any>;
  cuitModalRef: NgbModalRef | null = null;

  notaTipoId: number;
  notaTipoTexto: string;
  notaFacturaTexto: string;

  facturaTipoTexto: string;
  taxRole: number;
  consumidorTipoOptions = [
    { value: 'RESPONSABLE_INSCRIPTO', label: 'Responsable Inscripto' },
    { value: 'EXENTO', label: 'Exento' },
    { value: 'CONSUMIDOR_FINAL', label: 'Consumidor Final' },
    { value: 'MONOTRIBUTISTA', label: 'Monotributista' },
  ];
  /** precioUnitario admite string para poder detectar comas escritas por el usuario y pedir corrección. */
  detalleItems: Array<{ detalle: string; cantidad: number; precioUnitario: number | string }> = [
    { detalle: '', cantidad: 1, precioUnitario: 0 },
  ];

  mostrarCamposRG4004: boolean = false;
  validandoCuitTercero: boolean = false;
  cuitTerceroValido: boolean = false;
  mensajeValidacionCuit: string = '';

  facturaDataFormGroup = new UntypedFormGroup({
    concepto: new UntypedFormControl(2),
    consumidorDocumentoNumero: new UntypedFormControl(''),
    consumidorDocumentoTipo: new UntypedFormControl(''),
    consumidorTipo: new UntypedFormControl('CONSUMIDOR_FINAL'),
    consumidorDomicilio: new UntypedFormControl(''),
    consumidorNombre: new UntypedFormControl('', Validators.required),
    cuit: new UntypedFormControl(''),
    emisorDomicilio: new UntypedFormControl('',),
    emisorNombre: new UntypedFormControl('', Validators.required),
    fechaEmision: new UntypedFormControl('', Validators.required),
    fechaServicioDesde: new UntypedFormControl(''),
    fechaServicioHasta: new UntypedFormControl(''),
    fechaVencimientoPago: new UntypedFormControl('', Validators.required),
    importeExento: new UntypedFormControl(0, [Validators.min(0), Validators.max(2147483647)]),
    importeGravado: new UntypedFormControl(0, [Validators.min(0), Validators.max(2147483647)]),
    importeIva: new UntypedFormControl(0, [Validators.min(0), Validators.max(2147483647)]),
    importeTotal: new UntypedFormControl('', [Validators.required, Validators.min(0), Validators.max(2147483647)]),
    inicioActividades: new UntypedFormControl(''),
    iVATipoId: new UntypedFormControl(3),
    numeroFacturaAsociado: new UntypedFormControl(''),
    pdvAsociado: new UntypedFormControl(''),
    puntoDeVenta: new UntypedFormControl('', [Validators.min(0), Validators.max(2147483647)]),
    tipoDeFactura: new UntypedFormControl('', Validators.required),
    tipoFacturaAsociado: new UntypedFormControl(''),
    propietarioCuit: new UntypedFormControl(''),
    propietarioNombre: new UntypedFormControl(''),
    tipoFacturacion: new UntypedFormControl(1),
    habilitarRG4004: new UntypedFormControl(false),
    detalle: new UntypedFormControl('')
  });

  constructor(private router: Router,
    private toastService: ToastService,
    private dateService: DateService,
    private cuitService: CuitService,
    private facturasElectronicasService: FacturasElectronicasService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef) {
    for (let item in FacturaTypeEnum) {
      if (isNaN(Number(item))) {
        this.facturaType.push({ text: item, value: FacturaTypeEnum[item] });
      }
    }
    this.minDate = new Date()
    this.minDate.setDate(this.minDate.getDate() - 5);
    this.maxDate = new Date()
    this.maxDate.setDate(this.maxDate.getDate() + 5);
  }

  ngOnInit(): void {
    this.facturaDataFormGroup.controls['fechaEmision'].patchValue(this.dateService.transformToDateHash(new Date()));
    
    if (this.consumidores.length > 0 && this.consumidores[0].fullName != 'Consumidor Final') {
      this.consumidores.unshift({
        address: {
          city: '',
          state: '',
          streetAddress: 'No figura',
          zipCode: '',
        },      
        businessName: null,
        dateOfBirth: null,
        documentId: 0,
        documentType: 99,
        firstName: null,
        isPerson: null,
        lastName: null,
        personId: null,
        phones: null,
        fullName: 'Consumidor Final'
      })
    }
    
    if (this.consumidores.length >= 2) {
      this.updateConsumidor(1);
      const propietario = this.consumidores[1];
      if (propietario.documentType !== DocumentTypeEnum.CUIT || !propietario.documentId) {
        this.toastService.showToast(
          'warning',
          `El propietario "${propietario.fullName}" no tiene CUIT registrado. Completá los datos del consumidor manualmente.`
        );
      }
    }

    if (this.terceros == null){
      this.cuitService.getAll().subscribe((result) => {
        this.emisores = result;
        const lastCuit = this.lastInvoiceBy != null
          ? this.cuitService.limpiarCuit(String(this.lastInvoiceBy))
          : '';
        const idxUltimo = lastCuit
          ? result.findIndex(
              (e) => this.cuitService.limpiarCuit(String(e.cuitNumber)) === lastCuit,
            )
          : -1;

        if (idxUltimo >= 0) {
          this.emisorCuitSeleccionado = result[idxUltimo].cuitNumber;
          this.updateEmisor(idxUltimo);
        } else if (result.length == 1) {
          this.emisorCuitSeleccionado = result[0].cuitNumber;
          this.updateEmisor(0);
        } else {
          this.emisorCuitSeleccionado = null;
        }
      });
    } else {
      if (this.terceros.length == 1) {
        this.updateEmisor(0);
        this.updateFactura();
      }
    }
    
    const detallesPrefill = (Array.isArray(this.honorarioDetalle)
      ? this.honorarioDetalle
      : [this.honorarioDetalle])
      .map((d) => String(d ?? '').trim())
      .filter((d) => d.length > 0);

    if (detallesPrefill.length > 0) {
      const total =
        this.amount != null && this.amount !== ('' as unknown as number)
          ? Number(this.amount)
          : 0;
      // Prorratea el total entre los meses; el último ítem absorbe la diferencia de redondeo.
      const montoPorItem = Math.floor((total / detallesPrefill.length) * 100) / 100;
      this.detalleItems = detallesPrefill.map((detalle, i) => ({
        detalle,
        cantidad: 1,
        precioUnitario:
          i === detallesPrefill.length - 1
            ? Number((total - montoPorItem * (detallesPrefill.length - 1)).toFixed(2))
            : montoPorItem,
      }));
      this.recalcularTotalDesdeDetalle();
    } else if (this.amount) {
      this.facturaDataFormGroup.controls['importeTotal'].patchValue(this.amount);
      this.detalleItems = [{ detalle: '', cantidad: 1, precioUnitario: Number(this.amount) || 0 }];
    }
    
    if (this.facturaAsociada) {
      this.updateFactura();
      this.notaFacturaTexto = FacturaTypeEnum[this.facturaAsociada.tipoFactura]

      this.facturaDataFormGroup.patchValue({
        tipoFacturaAsociado: this.facturaAsociada.tipoFactura,
        pdvAsociado: this.facturaAsociada.puntoDeVenta,
        numeroFacturaAsociado: this.facturaAsociada.numeroComprobante,
        consumidorDocumentoNumero: this.facturaAsociada.consumidorDocumentoNumero,
        consumidorDocumentoTipo: DocumentTypeEnum[this.facturaAsociada.consumidorDocumentoTipo],
        consumidorDomicilio: this.facturaAsociada.consumidorDomicilio,
        consumidorNombre: this.facturaAsociada.consumidorNombre,
        cuit: this.facturaAsociada.emisorCUIT,
        propietarioCuit: this.facturaAsociada.propietarioCUIT,
        propietarioNombre: this.facturaAsociada.propietarioNombre,
        tipoFacturacion: this.facturaAsociada.tipoFacturacion,
        emisorDomicilio: this.facturaAsociada.emisorDomicilio,
        emisorNombre: this.facturaAsociada.emisorNombre,
        fechaServicioDesde: this.facturaAsociada.fechaServicioDesde,
        fechaServicioHasta: this.facturaAsociada.fechaServicioHasta,
        importeExento: this.facturaAsociada.importeOpEx,
        importeGravado: this.facturaAsociada.importeTributos,
        importeIva: this.facturaAsociada.importeIVA,
        importeTotal: this.facturaAsociada.precioTotal,
        inicioActividades: this.dateService.transformToDateHash(new Date(this.facturaAsociada.inicioActividades)),
        puntoDeVenta: this.facturaAsociada.puntoDeVenta,
        tipoDeFactura: this.notaTipoId,
      });
    }
    
    this.facturaDataFormGroup.get('habilitarRG4004')?.valueChanges.subscribe(habilitado => {
      this.mostrarCamposRG4004 = habilitado;
      this.actualizarValidacionesRG4004(habilitado);
    });
    
    this.facturaDataFormGroup.get('propietarioCuit')?.valueChanges
      .pipe(debounceTime(500))
      .subscribe(cuit => {
        if (cuit && cuit.length === 11) {
          this.validarCuitTercero(cuit);
        } else if (cuit && cuit.length > 0) {
          this.cuitTerceroValido = false;
          this.mensajeValidacionCuit = '⚠️ El CUIT debe tener exactamente 11 dígitos';
        } else {
          this.cuitTerceroValido = false;
          this.mensajeValidacionCuit = '';
        }
      });
  }
  actualizarValidacionesRG4004(habilitar: boolean) {
    const propietarioCuitControl = this.facturaDataFormGroup.get('propietarioCuit');
    const propietarioNombreControl = this.facturaDataFormGroup.get('propietarioNombre');
    
    if (habilitar) {
      propietarioCuitControl?.setValidators([
        Validators.required, 
        Validators.minLength(11), 
        Validators.maxLength(11),
        Validators.pattern(/^\d{11}$/)
      ]);
      propietarioNombreControl?.setValidators([Validators.required]);
      
      this.facturaDataFormGroup.get('tipoDeFactura')?.setValue(11); 
      this.facturaDataFormGroup.get('tipoDeFactura')?.disable();
      
      this.facturaDataFormGroup.get('concepto')?.setValue(2);
      
      console.log('✅ Habilitado modo RG 4004 - Factura por cuenta y orden de terceros');
    } else {
      propietarioCuitControl?.clearValidators();
      propietarioNombreControl?.clearValidators();
      
      propietarioCuitControl?.setValue('');
      propietarioNombreControl?.setValue('');
      
      this.facturaDataFormGroup.get('tipoDeFactura')?.enable();
      
      this.cuitTerceroValido = false;
      this.mensajeValidacionCuit = '';
      
      console.log('❌ Deshabilitado modo RG 4004 - Facturación normal');
    }
    
    propietarioCuitControl?.updateValueAndValidity();
    propietarioNombreControl?.updateValueAndValidity();
  }

  async validarCuitTercero(cuit: string) {
    this.validandoCuitTercero = true;
    this.mensajeValidacionCuit = 'Validando CUIT...';
    
    try {
      const validacion = await this.cuitService.validarCuitParaRG4004(cuit).toPromise();
      
      if (validacion.esValido) {
        this.cuitTerceroValido = true;
        this.mensajeValidacionCuit = '✅ CUIT válido para RG 4004';
        
        if (validacion.razonSocial) {
          this.facturaDataFormGroup.get('propietarioNombre')?.setValue(validacion.razonSocial);
        }
      } else {
        this.cuitTerceroValido = false;
        this.mensajeValidacionCuit = '❌ ' + (validacion.mensaje || 'CUIT no válido para RG 4004');
      }
    } catch (error) {
      if (this.validarFormatoCuit(cuit)) {
        this.cuitTerceroValido = true;
        this.mensajeValidacionCuit = '✅ Formato de CUIT válido';
      } else {
        this.cuitTerceroValido = false;
        this.mensajeValidacionCuit = '❌ CUIT inválido';
      }
      console.warn('Servicio de validación CUIT no disponible, usando validación local:', error);
    } finally {
      this.validandoCuitTercero = false;
    }
  }

  validarFormatoCuit(cuit: string): boolean {
    if (cuit.length !== 11) return false;

    const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let suma = 0;

    for (let i = 0; i < 10; i++) {
      suma += parseInt(cuit[i]) * multiplicadores[i];
    }

    const resto = suma % 11;
    const digitoVerificador = resto < 2 ? resto : 11 - resto;

    return digitoVerificador === parseInt(cuit[10]);
  }

  mostrarInfoRG4004() {
    this.toastService.showToast('info', `
📋 Facturación RG 4004 - Por cuenta y orden de terceros

✅ ¿Cuándo usar?
• Cuando administras propiedades de terceros
• El ingreso pertenece al propietario real
• Tu empresa solo actúa como intermediario

✅ Requisitos:
• CUIT válido del propietario real
• Nombre/razón social del propietario
• Se generará automáticamente una Factura C

✅ Efectos:
• El ingreso se atribuye fiscalmente al propietario
• Tu empresa no debe declarar este ingreso
• AFIP registra la operación correctamente
    `);
  }

  validarCampoRG4004(campo: string): boolean {
    const esRG4004 = this.facturaDataFormGroup.get('habilitarRG4004')?.value;
    
    if (!esRG4004) return false; 
    
    const control = this.facturaDataFormGroup.get(campo);
    return !!(control?.errors && control?.touched);
  }

  onSubmit() {
  this.facturaDataFormGroup.markAllAsTouched();

  if (this.facturaDataFormGroup.get('fechaEmision')?.hasError('ngbDate') ||
      this.facturaDataFormGroup.get('fechaVencimientoPago')?.hasError('ngbDate')) {
    const fechaEmisionErrors = this.facturaDataFormGroup.get('fechaEmision')?.errors?.ngbDate;
    const fechaVencErrors = this.facturaDataFormGroup.get('fechaVencimientoPago')?.errors?.ngbDate;
    if (fechaVencErrors?.minDate) {
      this.toastService.showToast(
        'warning',
        'La fecha de vencimiento debe ser posterior a la fecha de emision'
      );
    } else if (fechaEmisionErrors?.maxDate || fechaEmisionErrors?.minDate) {
      this.toastService.showToast(
        'warning',
        'No se pueden generar facturas fuera de un rango de 5 dias con respecto a la fecha actual'
      );
    } else if (fechaEmisionErrors) {
      this.toastService.showToast(
        'warning',
        'Corrija el formato de la fecha a AAAA-MM-DD'
      );
    }
    return;
  }

  const esRG4004 = this.facturaDataFormGroup.get('habilitarRG4004')?.value;

  if (esRG4004) {
    const propietarioCuit = this.facturaDataFormGroup.get('propietarioCuit')?.value;
    const propietarioNombre = this.facturaDataFormGroup.get('propietarioNombre')?.value;
    const tipoFacturacion = this.facturaDataFormGroup.get('tipoFacturacion')?.value;

    if (!propietarioCuit || !propietarioNombre || !tipoFacturacion) {
      this.toastService.showToast('warning', 'Para RG 4004 son obligatorios: CUIT, nombre del propietario y tipo de facturación');
      return;
    }

    if (!this.cuitTerceroValido) {
      this.toastService.showToast('warning', 'El CUIT del propietario no es válido para RG 4004');
      return;
    }
  }

  if (this.terceros == null) {
    const cuitEmisor = this.cuitService.limpiarCuit(
      String(this.facturaDataFormGroup.get('cuit')?.value ?? ''),
    );
    const emisorDelegado =
      cuitEmisor.length === 11 &&
      this.emisores.some(
        (e) => this.cuitService.limpiarCuit(String(e.cuitNumber)) === cuitEmisor,
      );
    if (!emisorDelegado) {
      this.toastService.showToast(
        'warning',
        'Para facturar, el emisor debe ser un CUIT ya dado de alta en el sistema.',
      );
      return;
    }
  }

  if (this.facturaDataFormGroup.invalid) {
    this.toastService.showToast('warning', 'Hay campos obligatorios sin completar');
    return;
  }

  if (this.hayDetalleMontoConComa) {
    this.toastService.showToast(
      'warning',
      'El monto del detalle no puede llevar coma. Corregilo usando punto para los decimales (ej: 1234.56).',
    );
    return;
  }

  this.submitted = true;

  let inicioActividadesDate = this.facturaDataFormGroup.value.inicioActividades
    ? this.dateService.transformToDateObject(
        this.facturaDataFormGroup.value.inicioActividades?.day,
        this.facturaDataFormGroup.value.inicioActividades?.month,
        this.facturaDataFormGroup.value.inicioActividades?.year
      )
    : null;

  let fechaEmisionDate = this.facturaDataFormGroup.value.fechaEmision
    ? this.dateService.transformToDateObject(
        this.facturaDataFormGroup.value.fechaEmision?.day,
        this.facturaDataFormGroup.value.fechaEmision?.month,
        this.facturaDataFormGroup.value.fechaEmision?.year
      )
    : null;

  let vencimientoDate = this.facturaDataFormGroup.value.fechaVencimientoPago
    ? this.dateService.transformToDateObject(
        this.facturaDataFormGroup.value.fechaVencimientoPago?.day,
        this.facturaDataFormGroup.value.fechaVencimientoPago?.month,
        this.facturaDataFormGroup.value.fechaVencimientoPago?.year
      )
    : null;

  if (fechaEmisionDate && vencimientoDate && vencimientoDate <= fechaEmisionDate) {
    this.toastService.showToast(
      'warning',
      'La fecha de vencimiento debe ser posterior a la fecha de emision'
    );
    this.submitted = false;
    return;
  }

  if (fechaEmisionDate) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const minEmision = new Date(hoy);
    minEmision.setDate(minEmision.getDate() - 5);
    const maxEmision = new Date(hoy);
    maxEmision.setDate(maxEmision.getDate() + 5);
    const fe = new Date(fechaEmisionDate);
    fe.setHours(0, 0, 0, 0);
    if (fe < minEmision || fe > maxEmision) {
      this.toastService.showToast(
        'warning',
        'La fecha de emision solo puede estar entre 5 dias antes y 5 dias despues de hoy'
      );
      this.submitted = false;
      return;
    }
  }

  let docTipo: number = this.resolveConsumidorDocumentoTipo();
  if (docTipo === 99) {
    this.facturaDataFormGroup.value.consumidorDocumentoNumero = 0;
  }

  let fechaServicioDesde = this.dateService.getNumberFromDateYYYYMMDD(this.dateService.getFirstDay(fechaEmisionDate));
  let fechaServicioHasta = this.dateService.getNumberFromDateYYYYMMDD(this.dateService.getLastDay(fechaEmisionDate));
  let fechaVto = this.dateService.getNumberFromDateYYYYMMDD(vencimientoDate);

  const detallePayload = this.detalleItems
    .map((item) => ({
      Detalle: String(item.detalle || '').trim(),
      Cantidad: Number(item.cantidad || 0),
      Monto: Number(item.precioUnitario || 0),
    }))
    .filter((item) => item.Detalle && item.Cantidad > 0 && item.Monto >= 0);

  if (detallePayload.length === 0) {
    this.toastService.showToast(
      'warning',
      'Debe cargar al menos un item con descripcion, cantidad y precio unitario.',
    );
    this.submitted = false;
    return;
  }

  const totalDesdeDetalle = detallePayload.reduce(
    (acc, item) => acc + (item.Cantidad * (item.Monto ?? 0)),
    0,
  );
  const importeTotalNumero =
    totalDesdeDetalle > 0
      ? Number(totalDesdeDetalle.toFixed(2))
      : (this.facturaDataFormGroup.value.importeTotal
          ? this.parseNumberWithComma(this.facturaDataFormGroup.value.importeTotal)
          : null);

  const detalleForSubmit: DetalleForAdd[] = detallePayload.length > 0
    ? detallePayload.map((item): DetalleForAdd => ({
        Detalle: item.Detalle,
        Cantidad: item.Cantidad,
        Monto: item.Monto,
      }))
    : (importeTotalNumero != null
        ? [{
            Detalle: 'Servicio',
            Cantidad: 1,
            Monto: importeTotalNumero,
          } as DetalleForAdd]
        : []);

  const facturaToSubmit: any = {
    CUIT: this.facturaDataFormGroup.value.cuit
      ? parseInt(this.facturaDataFormGroup.value.cuit.toString().replace(/[^0-9]/g, ''), 10)
      : null,
    PuntoDeVenta: this.facturaDataFormGroup.value.puntoDeVenta
      ? parseInt(this.facturaDataFormGroup.value.puntoDeVenta, 10)
      : null,
    TipoDeFactura: esRG4004
      ? 11
      : (this.facturaDataFormGroup.value.tipoDeFactura
          ? parseInt(this.facturaDataFormGroup.value.tipoDeFactura, 10)
          : null),
    Concepto: esRG4004
      ? 2
      : (this.facturaDataFormGroup.value.concepto
          ? parseInt(this.facturaDataFormGroup.value.concepto, 10)
          : 2),
    ConsumidorDocumentoTipo: docTipo,
    ConsumidorDocumentoNumero: docTipo === 99
      ? 0
      : this.parseConsumidorDocumentoNumero(this.facturaDataFormGroup.value.consumidorDocumentoNumero),
    FechaEmision: fechaEmisionDate,
    ImporteTotal: importeTotalNumero,
    ImporteGravado: this.facturaDataFormGroup.value.importeGravado
      ? this.parseNumberWithComma(this.facturaDataFormGroup.value.importeGravado)
      : 0,
    ImporteExento: this.facturaDataFormGroup.value.importeExento
      ? this.parseNumberWithComma(this.facturaDataFormGroup.value.importeExento)
      : 0,
    ImporteIva: this.facturaDataFormGroup.value.importeIva
      ? this.parseNumberWithComma(this.facturaDataFormGroup.value.importeIva)
      : 0,
    FechaServicioDesde: fechaServicioDesde,
    FechaServicioHasta: fechaServicioHasta,
    FechaVencimientoPago: fechaVto,
    IVATipoId: this.facturaDataFormGroup.value.iVATipoId
      ? parseInt(this.facturaDataFormGroup.value.iVATipoId, 10)
      : 3,
    HonorariosFacturados: this.facturaDataFormGroup.value.honorariosFacturados || null,
    PdvAsociado: this.facturaDataFormGroup.value.pdvAsociado
      ? parseInt(this.facturaDataFormGroup.value.pdvAsociado, 10)
      : null,
    TipoFacturaAsociado: this.facturaDataFormGroup.value.tipoFacturaAsociado
      ? parseInt(this.facturaDataFormGroup.value.tipoFacturaAsociado, 10)
      : null,
    NumeroFacturaAsociado: this.facturaDataFormGroup.value.numeroFacturaAsociado
      ? parseInt(this.facturaDataFormGroup.value.numeroFacturaAsociado, 10)
      : null,
    ConsumidorDomicilio: String(this.facturaDataFormGroup.value.consumidorDomicilio ?? ''),
    ConsumidorNombre: String(this.facturaDataFormGroup.value.consumidorNombre ?? ''),
    EmisorDomicilio: String(this.facturaDataFormGroup.value.emisorDomicilio ?? ''),
    EmisorNombre: String(this.facturaDataFormGroup.value.emisorNombre ?? ''),
    InicioActividades: inicioActividadesDate,
    ContractId: this.contractId,
    ...(this.feeId != null ? { FeeId: this.feeId } : {}),
    ...(this.paymentId != null ? { PaymentId: this.paymentId } : {}),
    // Compatibilidad: API .NET usa "Detalle" y API Nest usa "detallesForAdd"
    Detalle: detalleForSubmit,
    detallesForAdd: detalleForSubmit
  };

  if (esRG4004) {
    const propietarioCuit = this.facturaDataFormGroup.get('propietarioCuit')?.value;
    const propietarioNombre = this.facturaDataFormGroup.get('propietarioNombre')?.value;
    const tipoFacturacion = this.facturaDataFormGroup.get('tipoFacturacion')?.value;

    if (propietarioCuit && propietarioNombre && tipoFacturacion) {
      facturaToSubmit.PropietarioCUIT = parseInt(propietarioCuit.toString().replace(/[^0-9]/g, ''), 10);
      facturaToSubmit.PropietarioNombre = String(propietarioNombre);
      facturaToSubmit.TipoFacturacion = parseInt(tipoFacturacion, 10);
    }
  }

  console.log(' VERIFICANDO facturaToSubmit ANTES de enviar:');
  console.log('Es RG 4004:', esRG4004);
  console.log('PropietarioCUIT:', facturaToSubmit.PropietarioCUIT);
  console.log('PropietarioNombre:', facturaToSubmit.PropietarioNombre);
  console.log('TipoFacturacion:', facturaToSubmit.TipoFacturacion);
  console.log('📄 facturaToSubmit completo:', JSON.stringify(facturaToSubmit, null, 2));

  this.addFactura(facturaToSubmit);
}

  updateFactura(){
    if (this.facturaAsociada) {
      switch (this.facturaAsociada.tipoFactura) {
        case FacturaTypeEnum.A:
          this.notaTipoId = NotaTypeEnum.A;
          this.notaTipoTexto = 'Nota de Crédito ' + NotaTypeEnum[this.notaTipoId];
          break;
        case FacturaTypeEnum.B:
          this.notaTipoId = NotaTypeEnum.B;
          this.notaTipoTexto = 'Nota de Crédito ' + NotaTypeEnum[this.notaTipoId];
          break;
        case FacturaTypeEnum.C:
          this.notaTipoId = NotaTypeEnum.C;
          this.notaTipoTexto = 'Nota de Crédito ' + NotaTypeEnum[this.notaTipoId];
          break;
      }
    } else if (this.terceros) {
      switch (this.taxRole) {
        case TaxRole.ResponsableInscripto:
          this.facturaDataFormGroup.controls['iVATipoId'].patchValue(5);

          let name = this.facturaDataFormGroup.value.consumidorNombre;
          let id = this.facturaDataFormGroup.value.consumidorDocumentoNumero;
          let selectedConsumidor = this.terceros.find(t => t.fullName == name && t.documentId == id);

          if (selectedConsumidor?.taxRole) {
            this.facturaTipoTexto = 'A';
            this.facturaDataFormGroup.controls['tipoDeFactura'].patchValue(FacturaTypeEnum.A);
          } else {
            this.facturaTipoTexto = 'B';
            this.facturaDataFormGroup.controls['tipoDeFactura'].patchValue(FacturaTypeEnum.B);
          }
          this.facturaDataFormGroup.controls['importeExento'].addValidators(
            Validators.required,
          );
          this.facturaDataFormGroup.controls['importeGravado'].addValidators(
            Validators.required,
          );
          this.facturaDataFormGroup.controls['importeIva'].addValidators(
            Validators.required,
          );
          this.facturaDataFormGroup.controls['iVATipoId'].addValidators(
            Validators.required,
          );   
          break;
        case TaxRole.ResponsableMonotributo:
          this.facturaTipoTexto = 'C';
          this.facturaDataFormGroup.controls['tipoDeFactura'].patchValue(FacturaTypeEnum.C);
          this.facturaDataFormGroup.controls['importeExento'].clearValidators();   
          this.facturaDataFormGroup.controls['importeGravado'].clearValidators();   
          this.facturaDataFormGroup.controls['importeIva'].clearValidators();   
          this.facturaDataFormGroup.controls['iVATipoId'].clearValidators();   
          break;
      }
      this.facturaDataFormGroup.controls["importeExento"].updateValueAndValidity();
      this.facturaDataFormGroup.controls["importeGravado"].updateValueAndValidity();
      this.facturaDataFormGroup.controls["importeIva"].updateValueAndValidity();
      this.facturaDataFormGroup.controls["iVATipoId"].updateValueAndValidity();
    }
  }

  addFactura(facturaToSubmit) {
    console.log('=== DEBUG ANGULAR ===');
    console.log('📤 Datos que Angular está enviando:');
    console.log('PropietarioCUIT:', facturaToSubmit.PropietarioCUIT);
    console.log('PropietarioNombre:', facturaToSubmit.PropietarioNombre);
    console.log('TipoFacturacion:', facturaToSubmit.TipoFacturacion);
    console.log('📄 Objeto completo a enviar:');
    console.log(JSON.stringify(facturaToSubmit, null, 2));
    console.log('======================');
      
    this.facturasElectronicasService
      .createFactura(facturaToSubmit)
      .subscribe({
        next: (response: number) => {
          console.log('✅ Factura creada con ID:', response);
          this.toastService.showToast('success', 'Factura creada');
          this.router.navigateByUrl(`/facturas/${response}`);
          this.closeAction.emit();
        },
        error: (err) => {
          console.error('❌ Error al crear factura:', err);
          console.error('Error completo:', JSON.stringify(err, null, 2));
          if (err.error) {
            console.error('Error del servidor:', JSON.stringify(err.error, null, 2));
          }
          
          let msg = 'Ha ocurrido un error al crear la factura';
          
          // Extraer mensaje de error del backend - intentar múltiples formas
          if (err) {
            // Caso 1: Error HTTP con estructura NestJS estándar
            if (err.error) {
              if (err.error.message) {
                // Mensaje principal del backend NestJS (prioridad más alta)
                msg = err.error.message;
              } else if (err.error.error) {
                // Mensaje de error anidado
                msg = err.error.error;
              } else if (typeof err.error === 'string') {
                // Error como string directo
                msg = err.error;
              } else if (err.error === 'AFIP') {
                msg = 'No se pudo crear la factura por un error interno con AFIP';
              }
            }
            
            // Caso 2: Mensaje directo en el error
            if (err.message && msg === 'Ha ocurrido un error al crear la factura') {
              msg = err.message;
            }
            
            // Caso 3: Error de red o HTTP sin body
            if (!err.error && err.status) {
              if (err.status === 500) {
                msg = 'Error interno del servidor. Por favor, intente nuevamente.';
              } else if (err.status === 400) {
                msg = 'Error en los datos enviados. Verifique la información.';
              } else if (err.status === 404) {
                msg = 'Servicio no encontrado.';
              } else {
                msg = `Error ${err.status}: ${err.statusText || 'Error desconocido'}`;
              }
            }
          }
          
          // Limpiar mensajes muy largos o técnicos para el usuario
          if (msg.includes('Error al crear factura electrónica:')) {
            // Extraer solo la parte después de los dos puntos si es muy largo
            const parts = msg.split(':');
            if (parts.length > 1) {
              msg = parts.slice(1).join(':').trim();
            }
          }
          
          // Remover prefijos técnicos comunes
          msg = msg.replace(/^Error al crear factura electrónica:\s*/i, '');
          msg = msg.replace(/^Error:\s*/i, '');
          
          // Mostrar el mensaje en el toast
          this.toastService.showToast('error', msg);
          this.submitted = false;
        }
      });
  }

  validate(field: string) {
    return (
      this.facturaDataFormGroup.controls[field].errors &&
      this.facturaDataFormGroup.controls[field].touched
    );
  }

  updateEmisor(i: number) {    
    if (this.terceros == null){
      let emisor: CUITData = this.emisores[i];
      this.facturaDataFormGroup.patchValue({
        emisorNombre: emisor.socialReason,
        cuit: emisor.cuitNumber,
        emisorDomicilio: emisor.address,
        puntoDeVenta: emisor.sellPoints,
        inicioActividades: this.dateService.transformToDateHash(
          new Date(emisor.activityStartDate)),
      });
    }
    else{
      let tercero: Person = this.terceros[i];
      this.taxRole = tercero.taxRole;
      let address = this.getAddressString(tercero.address);      
      this.facturaDataFormGroup.patchValue({
        emisorNombre: tercero.fullName,
        cuit: tercero.documentId,
        emisorDomicilio: address,
      });
      this.updateFactura();
    }
  }

  updateConsumidor(i: number) {
    let consumidor: Person = this.consumidores[i];
    this.consumidorTipoDocumentoAfip = '';
    this.patchConsumidor(consumidor)
  }

  patchConsumidor(p: Person) {
    let doc: number = DocumentoFacturaEnum.CUIT;
    switch (+p.documentType) {
      case 1:
        doc = DocumentoFacturaEnum.DNI;
        break;
      case 2:
        doc = DocumentoFacturaEnum.CUIL;
        break;
      case 3:
      case 0:
        doc = DocumentoFacturaEnum.CUIT;
        break;
      case 99:
        doc = DocumentoFacturaEnum.CFINAL;
        break;
    }

    if (this.terceros != null && this.taxRole == TaxRole.ResponsableInscripto) {
      if (p.taxRole) {
        this.facturaTipoTexto = 'A';
        this.facturaDataFormGroup.controls['tipoDeFactura'].patchValue(FacturaTypeEnum.A);
      } else {
        this.facturaTipoTexto = 'B';
        this.facturaDataFormGroup.controls['tipoDeFactura'].patchValue(FacturaTypeEnum.B);
      }        
    }
    
    let address = this.getAddressString(p.address);

    this.facturaDataFormGroup.patchValue({
      consumidorNombre: p.fullName,
      consumidorDocumentoNumero: p.documentId,
      consumidorDomicilio: address,
      consumidorDocumentoTipo: doc,
      consumidorTipo: this.getConsumidorTipoDesdePersona(p),
    });
  }

  private getConsumidorTipoDesdePersona(p: Person): string {
    const taxRole = Number((p as any)?.taxRole ?? 0);
    if (taxRole === TaxRole.ResponsableInscripto) {
      return 'RESPONSABLE_INSCRIPTO';
    }
    if (taxRole === TaxRole.ResponsableMonotributo) {
      return 'MONOTRIBUTISTA';
    }
    const docType = Number((p as any)?.documentType ?? 0);
    if (docType === 99) {
      return 'CONSUMIDOR_FINAL';
    }
    return 'EXENTO';
  }

  getAddressString(address: Address){
    let result = address.streetAddress +
      (address.city ? ', ' + address.city : '') +
      (address.state ? ', ' + address.state : '');
    return result == 'null' ? "No figura" : result;
  }

  cancel() {
    this.cancelAction.emit();
  }

  search: OperatorFunction<any, any> = (text$: Observable<any>) =>
    text$.pipe(
      tap(() => (this.searching = true)),
      debounceTime(750),
      distinctUntilChanged(),
      switchMap((queryString) => {
        const q = this.buildRecipientsQuery(queryString || '');
        if (!q) {
          return of([]);
        }
        return this.cuitService.getAllRecipients(q).pipe(
          catchError(() => of([])),
        );
      }),
      tap(() => (this.searching = false))
    );

  /** PersonName (texto) o PersonIdentification (solo si el término son 11 dígitos = CUIT/DNI según backend). */
  private buildRecipientsQuery(raw: string): string | null {
    const q = (raw || '').trim();
    if (q.length < 3) {
      return null;
    }
    const soloDigitos = /^\d+$/.test(q);
    if (soloDigitos && q.length === 11) {
      return 'PersonIdentification=' + q;
    }
    return 'PersonName=' + encodeURIComponent(q);
  }

  setResult(event) {
    if (event && event.item) {
      this.patchConsumidor(event.item);
    }
  }

  onConsumidorBlur() {
    // Si hay texto escrito pero no se seleccionó ningún resultado, usar el texto como nombre
    if (this.searchString && this.searchString.trim() !== '') {
      const nombreEscrito = this.searchString.trim();
      
      // Solo actualizar si no hay un consumidor seleccionado o si el nombre cambió
      const nombreActual = this.facturaDataFormGroup.get('consumidorNombre')?.value;
      if (!nombreActual || nombreActual !== nombreEscrito) {
        this.facturaDataFormGroup.patchValue({
          consumidorNombre: nombreEscrito,
          consumidorDocumentoNumero: this.facturaDataFormGroup.get('consumidorDocumentoNumero')?.value || '',
          consumidorDomicilio: this.facturaDataFormGroup.get('consumidorDomicilio')?.value || 'No figura',
        });
      }
    }
  }

  clearSearch() {
    this.searchString = '';
    this.facturaDataFormGroup.patchValue({
      consumidorNombre: '',
      consumidorDocumentoNumero: '',
      consumidorDomicilio: '',
      consumidorTipo: 'CONSUMIDOR_FINAL',
    });
  }

  formatResult(consumidor: Person | string, selected = true) {
    if (typeof consumidor === 'string') {
      return consumidor;
    }
    if (selected) {
      return consumidor ? consumidor.fullName : '';
    }
    return consumidor ? `${consumidor.fullName} (${consumidor.documentId})` : '';
  }

  get emisoresFiltrados(): CUITData[] {
    const q = (this.filtroListaEmisor || '').trim().toLowerCase();
    if (!q) {
      return this.emisores;
    }
    const qDigits = q.replace(/\D/g, '');
    return this.emisores.filter((e) => {
      const cuit = String(e.cuitNumber || '').replace(/\D/g, '');
      const name = (e.socialReason || '').toLowerCase();
      const matchCuit = qDigits.length >= 2 && cuit.includes(qDigits);
      const matchName = name.includes(q);
      return matchCuit || matchName;
    });
  }

  onEmisorCuitSelected(cuit: string | null) {
    if (cuit == null || cuit === '') {
      return;
    }
    const i = this.emisores.findIndex((e) => String(e.cuitNumber) === String(cuit));
    if (i >= 0) {
      this.updateEmisor(i);
      this.datosAfipEmisorPendiente = null;
    }
  }

  limpiarSeleccionEmisor() {
    this.emisorCuitSeleccionado = null;
    this.filtroListaEmisor = '';
    this.datosAfipEmisorPendiente = null;
    this.facturaDataFormGroup.patchValue({
      emisorNombre: '',
      cuit: '',
      emisorDomicilio: '',
      puntoDeVenta: '',
      inicioActividades: '',
    });
  }

  buscarEmisorPorCuit() {
    const cuitLimpio = this.cuitService.limpiarCuit(this.cuitBuscadorEmisor);

    if (cuitLimpio.length !== 11) {
      if (cuitLimpio.length > 0) {
        this.toastService.showToast('warning', 'El CUIT debe tener exactamente 11 dígitos');
      }
      return;
    }

    if (!this.validarFormatoCuit(cuitLimpio)) {
      this.toastService.showToast('warning', 'CUIT inválido. Verifique el dígito verificador');
      return;
    }

    this.buscandoCuitEmisor = true;

    this.cuitService.obtenerInfoCuit(cuitLimpio).subscribe({
      next: (info) => {
        this.buscandoCuitEmisor = false;

        if (info) {
          const raw = info.raw || {};
          const nombreRaw = String(raw.nombre || '').trim();
          const apellidoRaw = String(raw.apellido || '').trim();
          const nombreCompletoRaw = `${nombreRaw} ${apellidoRaw}`.trim();

          const domicilios = Array.isArray(raw.domicilio) ? raw.domicilio : [];
          const domicilioFiscal =
            domicilios.find((d) =>
              String(d?.tipoDomicilio || '')
                .toUpperCase()
                .includes('FISCAL'),
            ) || domicilios[0];
          const direccionFiscal = String(
            domicilioFiscal?.direccion || '',
          ).trim();

          const razonSocial = (nombreCompletoRaw || info.razonSocial || '').trim();
          const domicilio = (info.domicilio || direccionFiscal || 'No figura').trim();

          const valoresPatch: any = {
            cuit: cuitLimpio,
            emisorNombre: razonSocial,
            emisorDomicilio: domicilio,
          };

          if (info.fechaInicioActividades) {
            try {
              const fechaInicio = new Date(info.fechaInicioActividades);
              if (!isNaN(fechaInicio.getTime())) {
                valoresPatch.inicioActividades = this.dateService.transformToDateHash(fechaInicio);
              }
            } catch {
              // fecha no parseable, continuar sin ella
            }
          } else if (raw.periodoActividadPrincipal) {
            const periodo = String(raw.periodoActividadPrincipal);
            if (/^\d{6}$/.test(periodo)) {
              const year = Number(periodo.substring(0, 4));
              const month = Number(periodo.substring(4, 6));
              if (year > 1900 && month >= 1 && month <= 12) {
                const fechaPeriodo = new Date(Date.UTC(year, month - 1, 1));
                valoresPatch.inicioActividades =
                  this.dateService.transformToDateHash(fechaPeriodo);
              }
            }
          }

          this.facturaDataFormGroup.patchValue(valoresPatch);

          const idxExistente = this.emisores.findIndex(
            (e) => this.cuitService.limpiarCuit(String(e.cuitNumber)) === cuitLimpio,
          );
          if (idxExistente >= 0) {
            this.emisorCuitSeleccionado = this.emisores[idxExistente].cuitNumber;
            this.updateEmisor(idxExistente);
            this.datosAfipEmisorPendiente = null;
          } else {
            this.emisorCuitSeleccionado = null;
            this.datosAfipEmisorPendiente = {
              cuitNumber: cuitLimpio,
              socialReason: razonSocial,
              address: domicilio,
              activityStartDate: valoresPatch.inicioActividades,
            };
          }

          let mensajeExito = `Emisor cargado desde ARCA: ${razonSocial || cuitLimpio}`;
          if (info.condicionIVA) mensajeExito += ` — IVA: ${info.condicionIVA}`;
          this.toastService.showToast('success', mensajeExito);
        } else {
          this.toastService.showToast('warning', 'No se encontro informacion del CUIT en ARCA');
        }
      },
      error: (err) => {
        this.buscandoCuitEmisor = false;
        if (err?.afipAuthFailure) {
          this.toastService.showToast(
            'error',
            'No se pudo consultar ARCA: credenciales o servicio no disponible. Revise el certificado delegado e intente mas tarde.',
          );
          return;
        }
        const mensaje =
          err?.error?.message ||
          err?.message ||
          'Error al consultar ARCA. Verifique el CUIT e intente nuevamente.';
        this.toastService.showToast('error', mensaje);
      }
    });
  }

  buscarConsumidorPorCuit() {
    const cuitLimpio = this.cuitService.limpiarCuit(this.cuitBuscadorConsumidor);

    if (cuitLimpio.length !== 11) {
      if (cuitLimpio.length > 0) {
        this.toastService.showToast('warning', 'El CUIT debe tener exactamente 11 dígitos');
      }
      return;
    }

    if (!this.validarFormatoCuit(cuitLimpio)) {
      this.toastService.showToast('warning', 'CUIT inválido. Verifique el dígito verificador');
      return;
    }

    this.buscandoCuitConsumidor = true;

    this.cuitService.obtenerInfoCuit(cuitLimpio).subscribe({
      next: (info) => {
        this.buscandoCuitConsumidor = false;

        if (info) {
          const raw = info.raw || {};
          const nombreRaw = String(raw.nombre || '').trim();
          const apellidoRaw = String(raw.apellido || '').trim();
          const nombreCompletoRaw = `${nombreRaw} ${apellidoRaw}`.trim();
          const domicilios = Array.isArray(raw.domicilio) ? raw.domicilio : [];
          const domicilioFiscal =
            domicilios.find((d) =>
              String(d?.tipoDomicilio || '')
                .toUpperCase()
                .includes('FISCAL'),
            ) || domicilios[0];
          const direccionFiscal = String(
            domicilioFiscal?.direccion || '',
          ).trim();

          const razonSocial = (nombreCompletoRaw || info.razonSocial || '').trim();
          const domicilio = (info.domicilio || direccionFiscal || 'No figura').trim();

          this.consumidorTipoDocumentoAfip = String(
            info?.raw?.tipoDocumento || '',
          ).trim();
          const tipoDocumento = this.mapAfipTipoDocumento(
            info?.raw?.tipoDocumento,
          );
          this.facturaDataFormGroup.patchValue({
            consumidorNombre: razonSocial,
            consumidorDocumentoNumero: cuitLimpio,
            consumidorDomicilio: domicilio,
            consumidorDocumentoTipo: tipoDocumento,
            consumidorTipo: this.mapCondicionIvaToConsumidorTipo(info?.condicionIVA),
          });
          this.searchString = razonSocial;

          let mensajeExito = `Consumidor cargado desde ARCA: ${razonSocial || cuitLimpio}`;
          if (info.condicionIVA) mensajeExito += ` — IVA: ${info.condicionIVA}`;
          this.toastService.showToast('success', mensajeExito);
        } else {
          this.toastService.showToast('warning', 'No se encontro informacion del CUIT en ARCA');
        }
      },
      error: (err) => {
        this.buscandoCuitConsumidor = false;
        if (err?.afipAuthFailure) {
          this.toastService.showToast(
            'error',
            'No se pudo consultar ARCA: credenciales o servicio no disponible. Revise el certificado delegado e intente mas tarde.',
          );
          return;
        }
        const mensaje =
          err?.error?.message ||
          err?.message ||
          'Error al consultar ARCA. Verifique el CUIT e intente nuevamente.';
        this.toastService.showToast('error', mensaje);
      }
    });
  }

  private mapCondicionIvaToConsumidorTipo(condicionIva: unknown): string {
    const c = String(condicionIva || '').toUpperCase();
    if (c.includes('INSCRIPTO')) return 'RESPONSABLE_INSCRIPTO';
    if (c.includes('MONOTRIB')) return 'MONOTRIBUTISTA';
    if (c.includes('CONSUMIDOR')) return 'CONSUMIDOR_FINAL';
    if (c.includes('EXENTO')) return 'EXENTO';
    return 'CONSUMIDOR_FINAL';
  }

  agregarDetalleItem() {
    this.detalleItems.push({ detalle: '', cantidad: 1, precioUnitario: 0 });
    this.recalcularTotalDesdeDetalle();
  }

  eliminarDetalleItem(index: number) {
    if (this.detalleItems.length <= 1) {
      this.detalleItems[0] = { detalle: '', cantidad: 1, precioUnitario: 0 };
    } else {
      this.detalleItems.splice(index, 1);
    }
    this.recalcularTotalDesdeDetalle();
  }

  onDetalleItemChange() {
    this.recalcularTotalDesdeDetalle();
  }

  /** True si el usuario escribió el monto con coma; AFIP exige punto decimal y hay que pedir corrección. */
  detalleMontoTieneComa(item: { precioUnitario: number | string }): boolean {
    return String(item?.precioUnitario ?? '').includes(',');
  }

  get hayDetalleMontoConComa(): boolean {
    return this.detalleItems.some((item) => this.detalleMontoTieneComa(item));
  }

  getSubtotalDetalle(item: { cantidad: number; precioUnitario: number | string }): number {
    const cantidad = Number(item?.cantidad || 0);
    const precio = Number(item?.precioUnitario || 0);
    if (!Number.isFinite(precio) || !Number.isFinite(cantidad)) {
      return 0;
    }
    return Number((cantidad * precio).toFixed(2));
  }

  get totalDetalleFactura(): number {
    return Number(
      this.detalleItems
        .reduce((acc, item) => acc + this.getSubtotalDetalle(item), 0)
        .toFixed(2),
    );
  }

  private recalcularTotalDesdeDetalle() {
    const total = this.totalDetalleFactura;
    if (total > 0) {
      this.facturaDataFormGroup.patchValue(
        { importeTotal: total },
        { emitEvent: false },
      );
    }
  }

  private mapAfipTipoDocumento(tipoDocumentoAfip: string): number {
    const tipo = String(tipoDocumentoAfip || '').toUpperCase().trim();
    if (tipo === 'DNI') return 96;
    if (tipo === 'CUIT') return 80;
    if (tipo === 'CUIL') return 86;
    return 80;
  }

  getTipoDocumentoLabel(tipoDocumento: number | string): string {
    const tipo = Number(tipoDocumento);
    if (tipo === 96) return 'DNI';
    if (tipo === 80) return 'CUIT';
    if (tipo === 86) return 'CUIL';
    if (tipo === 99) return 'Consumidor Final';
    return String(tipoDocumento ?? '');
  }

  /** Conserva etiqueta legible de AFIP/ARCA si viene informada; fallback al tipo local. */
  getTipoDocumentoConsumidorDisplay(): string {
    const tipoAfip = String(this.consumidorTipoDocumentoAfip || '').toUpperCase().trim();
    if (tipoAfip) {
      if (tipoAfip === 'DNI') return 'DNI';
      if (tipoAfip === 'PAS' || tipoAfip === 'PASAPORTE') return 'Pasaporte';
      if (tipoAfip === 'LC') return 'Libreta Cívica';
      if (tipoAfip === 'LE') return 'Libreta de Enrolamiento';
      if (tipoAfip === 'CI') return 'Cédula';
      if (tipoAfip === 'CUIT') return 'CUIT';
      if (tipoAfip === 'CUIL') return 'CUIL';
      if (tipoAfip === 'CDI') return 'CDI';
      return tipoAfip.charAt(0) + tipoAfip.slice(1).toLowerCase();
    }
    return this.getTipoDocumentoLabel(this.facturaDataFormGroup.value.consumidorDocumentoTipo) || '—';
  }

  /** Muestra la fecha de inicio de actividades del emisor en formato dd/MM/yyyy */
  get inicioActividadesDisplay(): string {
    const val = this.facturaDataFormGroup.value.inicioActividades;
    if (!val || typeof val !== 'object') return '—';
    const { year, month, day } = val as { year: number; month: number; day: number };
    if (!year || !month || !day) return '—';
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  }

  anadirEmisorDesdeAfip() {
    if (!this.datosAfipEmisorPendiente) {
      return;
    }
    this.openCuitForm(this.datosAfipEmisorPendiente);
  }

  /** Si se pasa borrador, abre el alta de CUIT precargada (p. ej. desde AFIP). Sin argumentos: alta vacía. */
  openCuitForm(draft?: CuitFormInitialDraft | null) {
    this.cuitFormInitialDraft = draft === undefined ? null : (draft ?? null);
    // Forzar change detection para que el ng-template vea el nuevo valor antes de que se monte el modal
    this.cdr.detectChanges();
    this.cuitModalRef = this.modalService.open(this.cuitFormTemplate, { size: 'lg', centered: true });
  }

  closeCuitForm() {
    // Guardar el CUIT recién agregado (si vino desde AFIP) antes de limpiar el estado
    const cuitRecienAgregado = this.datosAfipEmisorPendiente?.cuitNumber ?? null;
    this.cuitFormInitialDraft = null;
    this.datosAfipEmisorPendiente = null;
    if (this.cuitModalRef) {
      this.cuitModalRef.close();
      this.cuitModalRef = null;
    }
    this.cuitService.getAll().subscribe((result) => {
      this.emisores = result;
      if (result.length === 0) {
        return;
      }
      // Intentar seleccionar el CUIT que se acaba de agregar; si no se sabe, el último
      const idxByCuit = cuitRecienAgregado
        ? result.findIndex(
            (e) => this.cuitService.limpiarCuit(String(e.cuitNumber)) === cuitRecienAgregado,
          )
        : -1;
      const i = idxByCuit >= 0 ? idxByCuit : result.length - 1;
      this.emisorCuitSeleccionado = result[i].cuitNumber;
      this.updateEmisor(i);
    });
  }

  cancelCuitForm() {
    this.cuitFormInitialDraft = null;
    if (this.cuitModalRef) {
      this.cuitModalRef.dismiss();
      this.cuitModalRef = null;
    }
  }

  /**
   * Convierte números con formato argentino (coma como decimal) o internacional (punto) a número
   * AFIP solo acepta punto decimal, así que convertimos comas a puntos
   * Maneja tanto "1.234,56" como "1234,56" o "1234.56" o "1,234.56"
   */
  parseNumberWithComma(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    
    const str = String(value).trim();
    
    // Si ya es un número, retornarlo
    if (typeof value === 'number') {
      return value;
    }
    
    // Remover espacios
    let cleaned = str.replace(/\s/g, '');
    
    // Detectar formato: si tiene punto y coma, el último separador es el decimal
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    
    if (lastComma > lastDot) {
      // Formato argentino: "1.234,56" o "1234,56"
      // Remover puntos (separadores de miles) y reemplazar coma por punto (AFIP usa punto)
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      // Formato internacional: "1,234.56" o "1234.56"
      // Remover comas (separadores de miles), el punto ya es decimal
      cleaned = cleaned.replace(/,/g, '');
    } else if (lastComma !== -1) {
      // Solo tiene coma, convertir a punto (AFIP usa punto)
      cleaned = cleaned.replace(',', '.');
    }
    
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Normaliza el input numérico cuando el usuario sale del campo (blur)
   * Convierte comas a puntos porque AFIP solo acepta punto decimal
   * Permite escribir con coma pero la normaliza a punto
   */
  normalizeNumberInput(fieldName: string) {
    const control = this.facturaDataFormGroup.get(fieldName);
    if (!control) return;
    
    const value = control.value;
    if (value && typeof value === 'string') {
      const trimmed = value.trim();
      
      // Si tiene coma, convertirla a punto (AFIP usa punto decimal)
      if (trimmed.includes(',')) {
        // Detectar si es formato argentino (1.234,56) o simple (1234,56)
        const lastComma = trimmed.lastIndexOf(',');
        const lastDot = trimmed.lastIndexOf('.');
        
        let normalized: string;
        if (lastComma > lastDot) {
          // Formato argentino: remover puntos de miles y convertir coma a punto
          normalized = trimmed.replace(/\./g, '').replace(',', '.');
        } else {
          // Solo coma decimal, convertir a punto
          normalized = trimmed.replace(',', '.');
        }
        
        // Actualizar el valor sin disparar eventos para evitar loops
        if (normalized !== trimmed) {
          control.setValue(normalized, { emitEvent: false });
        }
      }
      
      // Validar que solo tenga números y punto
      const isValid = /^[\d.]+$/.test(trimmed.replace(',', '.'));
      if (!isValid && trimmed !== '') {
        // Limpiar caracteres no válidos
        const cleaned = trimmed.replace(/[^\d.,]/g, '');
        if (cleaned !== trimmed) {
          control.setValue(cleaned, { emitEvent: false });
        }
      }
    }
  }

  private parseConsumidorDocumentoNumero(value: unknown): number {
    const n = parseInt(String(value ?? '').replace(/\D/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  }

  private resolveConsumidorDocumentoTipo(): number {
    const raw = this.facturaDataFormGroup.value.consumidorDocumentoTipo;
    const nroLimpio = String(
      this.facturaDataFormGroup.value.consumidorDocumentoNumero ?? '',
    ).replace(/\D/g, '');

    const direct = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
    if (Number.isInteger(direct) && direct > 0) {
      return direct;
    }

    const rawStr = String(raw ?? '').trim();
    if (rawStr) {
      const mapped = (DocumentoFacturaEnum as any)[rawStr];
      const fromEnum = Number(mapped);
      if (Number.isInteger(fromEnum) && fromEnum > 0) {
        return fromEnum;
      }
      const upper = rawStr.toUpperCase();
      if (upper === 'CUIT' || upper === '80') return DocumentoFacturaEnum.CUIT;
      if (upper === 'CUIL' || upper === '86') return DocumentoFacturaEnum.CUIL;
      if (upper === 'DNI' || upper === '96') return DocumentoFacturaEnum.DNI;
      if (
        upper === 'CONSUMIDOR FINAL' ||
        upper === 'CFINAL' ||
        upper === '99'
      ) {
        return DocumentoFacturaEnum.CFINAL;
      }
    }
    if (/^\d{11}$/.test(nroLimpio)) {
      return DocumentoFacturaEnum.CUIT;
    }

    return DocumentoFacturaEnum.CUIT;
  }

  get minFechaVencimiento(): { year: number; month: number; day: number } | null {
    const emision = this.facturaDataFormGroup.get('fechaEmision')?.value;
    if (!emision?.year || !emision?.month || !emision?.day) {
      return null;
    }
    return { year: emision.year, month: emision.month, day: emision.day };
  }
}